import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  PhotoCamera as PhotoIcon,
  Delete as DeleteIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';
const MIN_PASSWORD_LENGTH = 12;

interface ProfileData {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl: string | null;
  createdAt: string;
  lastLogin: string | null;
  teamName: string | null;
}

const formatDate = (stamp: string | null) => {
  if (!stamp) return 'Never';
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return stamp;
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const initials = (name: string) =>
  name.split(' ').map(part => part[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

export const Profile: React.FC = () => {
  const { token, refreshUser } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data: ProfileData = await response.json();
        setProfile(data);
        setName(data.name);
        setEmail(data.email);
      } else {
        setError('Failed to load your profile');
      }
    } catch (err) {
      setError('Network error loading your profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const emailChanged = profile ? email.trim().toLowerCase() !== profile.email : false;
  const nameChanged = profile ? name.trim() !== profile.name : false;

  const handleSaveDetails = async () => {
    setError('');
    setSuccess('');
    setSavingDetails(true);

    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          // Only sent when the address actually changes
          ...(emailChanged ? { currentPassword: emailPassword } : {}),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(emailChanged ? 'Details updated — use your new email address to sign in.' : 'Details updated');
        setEmailPassword('');
        load();
        // The header shows the name, so refresh the session user too
        refreshUser();
      } else {
        setError(data.error || 'Could not save your details');
      }
    } catch (err) {
      setError('Network error saving your details');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('The new passwords do not match');
      return;
    }

    setSavingPassword(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        const ended = data.otherSessionsEndedCount || 0;
        setSuccess(
          ended > 0
            ? `Password updated. ${ended} other session${ended === 1 ? '' : 's'} signed out.`
            : 'Password updated'
        );
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.error || 'Could not change your password');
      }
    } catch (err) {
      setError('Network error changing your password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setUploadingAvatar(true);

    try {
      const response = await fetch(`${API_URL}/api/profile/avatar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': file.type },
        body: file,
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Photo updated');
        load();
        refreshUser();
      } else {
        setError(data.error || 'Could not upload the photo');
      }
    } catch (err) {
      setError('Network error uploading the photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setError('');
    try {
      await fetch(`${API_URL}/api/profile/avatar`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setSuccess('Photo removed');
      load();
      refreshUser();
    } catch (err) {
      setError('Network error removing the photo');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const passwordTooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Your account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These settings apply to you alone. Platform connections and posts are shared with
        the rest of {profile?.teamName || 'your team'}.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Photo</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
            <Avatar
              src={profile?.avatarUrl || undefined}
              sx={{ width: 88, height: 88, fontSize: '2rem', bgcolor: 'primary.main' }}
            >
              {initials(profile?.name || '?')}
            </Avatar>

            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PhotoIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? 'Uploading…' : profile?.avatarUrl ? 'Replace' : 'Upload photo'}
                </Button>
                {profile?.avatarUrl && (
                  <Tooltip title="Remove photo">
                    <IconButton size="small" onClick={handleRemoveAvatar}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                JPEG, PNG, WebP or GIF, up to 5 MB.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarSelect}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Details</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              helperText={emailChanged ? 'You will sign in with this address from now on.' : 'Used to sign in.'}
            />

            {/* Changing the login identifier is a credential change, so it needs the password */}
            {emailChanged && (
              <TextField
                label="Current password"
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                fullWidth
                size="small"
                helperText="Required to change your email address"
              />
            )}

            <Box>
              <Button
                variant="contained"
                onClick={handleSaveDetails}
                disabled={
                  savingDetails ||
                  (!nameChanged && !emailChanged) ||
                  !name.trim() ||
                  (emailChanged && !emailPassword)
                }
              >
                {savingDetails ? 'Saving…' : 'Save changes'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Role</Typography>
              <Chip size="small" label={profile?.role === 'admin' ? 'Admin' : profile?.role === 'demo' ? 'Demo' : 'User'} sx={{ mt: 0.5 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Member since</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{formatDate(profile?.createdAt || null)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Last sign-in</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{formatDate(profile?.lastLogin || null)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Password</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changing your password signs out your other sessions, so anyone using a stolen
            token loses access.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
            <TextField
              label="Current password"
              type={showPasswords ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPasswords(!showPasswords)} edge="end">
                      {showPasswords ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New password"
              type={showPasswords ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              error={passwordTooShort}
              helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
            />
            <TextField
              label="Confirm new password"
              type={showPasswords ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              error={mismatch}
              helperText={mismatch ? 'Passwords do not match' : ' '}
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleChangePassword}
                disabled={
                  savingPassword ||
                  !currentPassword ||
                  newPassword.length < MIN_PASSWORD_LENGTH ||
                  newPassword !== confirmPassword
                }
              >
                {savingPassword ? 'Updating…' : 'Change password'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
